import type { OnboardingDraftItem } from '@hostesa/api-client';
import { useReviewDraftItem } from '@hostesa/api-client';
import { useT } from '@hostesa/i18n';
import { Button, Input, Label, StatusBadge } from '@hostesa/ui';
import { useState } from 'react';
import { namespace } from './i18n';
import { asNumber, asText, describeValue, itemStatusVariant } from './lib';

/**
 * DraftItemReview — a single extracted item with its source citations,
 * confidence badge, and accept/edit/reject controls. Edits send the corrected
 * value; the server records the reviewer.
 */
export function DraftItemReview({
  tenantId,
  hotelId,
  runId,
  item,
}: {
  tenantId: string;
  hotelId: string;
  runId: string;
  item: OnboardingDraftItem;
}) {
  const { t } = useT(namespace);
  const review = useReviewDraftItem(tenantId, hotelId);

  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(
    describeValue(item.reviewedValue ?? item.extractedValue) ?? '',
  );
  const [reason, setReason] = useState('');

  const confidence = asNumber(item.extractedConfidence);
  const extracted = describeValue(item.extractedValue);
  const reviewed = describeValue(item.reviewedValue);
  const reviewReason = asText(item.reviewReason);
  const category = asText(item.itemCategory);

  const lowConfidence = confidence !== null && confidence < 0.6;
  const pending = item.status === 'pending_review';

  function accept() {
    review.mutate({ runId, itemId: item.id, body: { action: 'accept' } });
  }
  function reject() {
    review.mutate({
      runId,
      itemId: item.id,
      body: { action: 'reject', reason: reason || undefined },
    });
  }
  function saveEdit() {
    // The generated `value` field is a `Record<string, never>` placeholder for a
    // free-form extracted value; the backend accepts an arbitrary object.
    const editedValue = { value } as unknown as Record<string, never>;
    review.mutate(
      {
        runId,
        itemId: item.id,
        body: { action: 'edit', value: editedValue, reason: reason || undefined },
      },
      { onSuccess: () => setEditing(false) },
    );
  }

  return (
    <li
      data-testid={`onboarding-draft-item-${item.id}`}
      className="flex flex-col gap-3 rounded-lg border border-[var(--color-warm-stone-soft)] p-4"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded bg-[var(--color-stone-100)] px-2 py-0.5 text-xs text-[var(--color-charcoal)]">
          {t(`itemType.${item.itemType}`)}
        </span>
        {category && (
          <span className="text-xs text-[var(--color-stone-mid)]">
            {t('review.itemCategory')}: {category}
          </span>
        )}
        {item.requiredField && (
          <StatusBadge
            data-testid={`onboarding-draft-required-${item.id}`}
            status="warning"
            label={t('review.required')}
          />
        )}
        <StatusBadge
          data-testid={`onboarding-draft-status-${item.id}`}
          status={itemStatusVariant(item.status)}
          label={t(`itemStatus.${item.status}`)}
        />
        {confidence !== null && (
          <StatusBadge
            data-testid={`onboarding-draft-confidence-${item.id}`}
            status={lowConfidence ? 'warning' : 'neutral'}
            label={`${t('review.confidence')} ${(confidence * 100).toFixed(0)}%`}
          />
        )}
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-xs uppercase tracking-wide text-[var(--color-stone-mid)]">
          {t('review.extractedValue')}
        </span>
        <p className="text-sm text-[var(--color-charcoal)]">
          {extracted ?? (
            <span className="text-[var(--color-stone-mid)]">{t('review.noValue')}</span>
          )}
        </p>
        {reviewed && reviewed !== extracted && (
          <>
            <span className="mt-1 text-xs uppercase tracking-wide text-[var(--color-stone-mid)]">
              {t('review.reviewedValue')}
            </span>
            <p className="text-sm text-[var(--color-charcoal)]">{reviewed}</p>
          </>
        )}
        {reviewReason && (
          <p className="text-xs text-[var(--color-warm-stone-deep)]">
            {t('review.reviewReason')}: {reviewReason}
          </p>
        )}
      </div>

      {item.sourceUrls.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-xs uppercase tracking-wide text-[var(--color-stone-mid)]">
            {t('review.sources')}
          </span>
          <ul className="flex flex-col gap-0.5">
            {item.sourceUrls.map((url) => (
              <li key={url}>
                <a
                  data-testid={`onboarding-draft-source-${item.id}`}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-[var(--color-charcoal)] underline"
                >
                  {t('review.sourceLink')}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {editing && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-1">
            <Label htmlFor={`draft-edit-${item.id}`}>{t('review.editValueLabel')}</Label>
            <Input
              id={`draft-edit-${item.id}`}
              data-testid={`onboarding-draft-edit-input-${item.id}`}
              placeholder={t('review.editValuePlaceholder')}
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor={`draft-reason-${item.id}`}>{t('review.editReasonLabel')}</Label>
            <Input
              id={`draft-reason-${item.id}`}
              data-testid={`onboarding-draft-reason-input-${item.id}`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>
      )}

      {pending && (
        <div className="flex flex-wrap gap-2">
          {!editing ? (
            <>
              <Button
                size="sm"
                variant="primary"
                data-testid={`onboarding-draft-accept-btn-${item.id}`}
                disabled={review.isPending}
                onClick={accept}
              >
                {t('review.accept')}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                data-testid={`onboarding-draft-edit-btn-${item.id}`}
                onClick={() => setEditing(true)}
              >
                {t('review.edit')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                data-testid={`onboarding-draft-reject-btn-${item.id}`}
                disabled={review.isPending}
                onClick={reject}
              >
                {t('review.reject')}
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                variant="primary"
                data-testid={`onboarding-draft-save-btn-${item.id}`}
                disabled={review.isPending}
                onClick={saveEdit}
              >
                {t('review.saveEdit')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                data-testid={`onboarding-draft-cancel-btn-${item.id}`}
                onClick={() => setEditing(false)}
              >
                {t('review.cancel')}
              </Button>
            </>
          )}
        </div>
      )}
    </li>
  );
}
